import { useState, useCallback, useEffect, useMemo } from 'react';


export interface UseTableSelectionOptions<T> {
  data: T[];
  getRowId: (row: T) => string | number;
  selectable: boolean;
  onSelectionChange?: (selected: T[]) => void;
  isLoading?: boolean;
  isRowSelectable?: (row: T) => boolean;
  /**
   * Opt-in controlled mode: when provided, the consumer owns the selection and this hook mirrors
   * it. Use it when the consumer needs to correct what the user selected (cap a maximum, clear the
   * selection after an action, drop rows that a filter change made irrelevant) — otherwise the
   * internal set and the consumer's would drift apart. Omitting it keeps the uncontrolled default.
   */
  selectedRowIds?: Set<string | number>;
}

export interface UseTableSelectionReturn<T> {
  selectedIds: Set<string | number>;
  allVisibleSelected: boolean;
  isIndeterminate: boolean;
  hasSelectableRows: boolean;
  toggleSelectAll: () => void;
  toggleRow: (row: T) => void;
}

/**
 * Hook to manage table row selection state and logic
 * Handles single row selection, select all, and selection callbacks.
 *
 * 3-state toggle behavior for the header checkbox:
 *   - no selection (empty) → click → all visible selected
 *   - partial selection (indeterminate) → click → all visible selected
 *   - all visible selected → click → none selected
 */
export function useTableSelection<T extends Record<string, any>>(
  options: UseTableSelectionOptions<T>,
): UseTableSelectionReturn<T> {
  const {
    data, getRowId, selectable, onSelectionChange, isLoading = false, isRowSelectable,
    selectedRowIds,
  } = options;

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // Controlled mode: the consumer's set wins. The comparison is by content (not by reference) so a
  // consumer that rebuilds the set on every render doesn't trigger an endless update loop.
  useEffect(() => {
    if (!selectedRowIds) return;

    setSelectedIds((current) => (
      current.size === selectedRowIds.size && [...selectedRowIds].every((id) => current.has(id))
        ? current
        : new Set(selectedRowIds)
    ));
  }, [selectedRowIds]);

  const selectableData = useMemo(
    () => (isRowSelectable ? data.filter(isRowSelectable) : data),
    [data, isRowSelectable],
  );

  const allVisibleSelected = useMemo(() => (
    selectable &&
      selectableData.length > 0 &&
      selectableData.every((row) => selectedIds.has(getRowId(row)))
  ), [selectable, selectableData, selectedIds, getRowId]);

  const isIndeterminate = useMemo(
    () => selectable && selectedIds.size > 0 && !allVisibleSelected,
    [selectable, selectedIds.size, allVisibleSelected],
  );

  // Both handlers compute the next selection from `selectedIds` (closure) instead of from the
  // setState updater argument, so the updater stays pure. Invoking onSelectionChange inside the
  // updater made it a side effect: React StrictMode runs updaters twice in development, firing
  // the consumer callback (and any toast/request it triggers) twice per click. Handlers run from
  // DOM events, so reading state from the closure is safe — a re-render precedes the next click.
  const toggleSelectAll = useCallback(() => {
    if (!selectable || isLoading) return;

    const isAllSelected = selectableData.every((row) => selectedIds.has(getRowId(row)));
    const nextSelectedIds = new Set(selectedIds);

    if (isAllSelected) {
      // all → none
      for (const row of selectableData) nextSelectedIds.delete(getRowId(row));
    } else {
      // none or partial → all selectable
      for (const row of selectableData) nextSelectedIds.add(getRowId(row));
    }

    setSelectedIds(nextSelectedIds);
    onSelectionChange?.(data.filter((row) => nextSelectedIds.has(getRowId(row))));
  }, [selectable, data, selectableData, selectedIds, getRowId, onSelectionChange, isLoading]);

  const toggleRow = useCallback(
    (row: T) => {
      if (!selectable || isLoading) return;

      const id = getRowId(row);
      const nextSelectedIds = new Set(selectedIds);

      if (nextSelectedIds.has(id)) {
        nextSelectedIds.delete(id);
      } else {
        nextSelectedIds.add(id);
      }

      setSelectedIds(nextSelectedIds);
      onSelectionChange?.(data.filter((r) => nextSelectedIds.has(getRowId(r))));
    },
    [selectable, selectedIds, getRowId, data, onSelectionChange, isLoading],
  );

  return {
    selectedIds,
    allVisibleSelected,
    isIndeterminate,
    hasSelectableRows: selectableData.length > 0,
    toggleSelectAll,
    toggleRow,
  };
}
